import { useState, useEffect, useCallback } from "react";
import { useRedirectError } from "../utils/useRedirectError";
import { useNavigate, Link } from "react-router-dom";
import {
  login,
  googleLoginUrl,
  // checkUserByEmail
} from "../API/auth";
import { isSignedIn, setSignedIn } from "../utils/authStorage";
import { setEmailVerificationState } from "../utils/emailVerificationStorage";
import { consumePostLoginRedirect } from "../utils/postLoginRedirect";
import { buttonloginstyle, formatAuthError } from "../help/Utils";
import {
  GoogleGIcon,
  GoogleSignInCustomButton,
} from "../components/GoogleSignInCustomButton";
import secure from "../assets/secure.png";

function GoogleSignInHint() {
  return (
    <div
      role="region"
      aria-labelledby="google-signin-hint-title"
      aria-live="polite"
      className="mt-4 overflow-hidden rounded-xl border border-[#1B365D]/12 bg-gradient-to-br from-[#1B365D]/[0.06] via-white to-slate-50/90 shadow-[0_2px_8px_rgba(27,54,93,0.06)]"
    >
      <div className="flex items-start gap-4 p-4 sm:p-5">
        {/* Standard-color G on white + neutral stroke (Identity branding). */}
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-[#747775] bg-white">
          <GoogleGIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            id="google-signin-hint-title"
            className="text-base font-semibold tracking-tight text-[#1B365D]"
          >
            This account uses Google sign-in
          </p>
          <p className="mt-2 text-sm leading-relaxed text-[#1B365D]/80">
            Use{" "}
            <span className="font-semibold text-[#1B365D]">Continue with Google</span>{" "}
            below. If you’d rather use email and password, set one up first with{" "}
            <Link
              to="/reset-password"
              className="font-semibold text-[#00B894] underline decoration-[#00B894]/35 underline-offset-[3px] transition hover:decoration-[#00B894]"
            >
              Forgot password
            </Link>
            , then sign in here.
          </p>
        </div>
      </div>
    </div>
  );
}

function NoPasswordHint() {
  return (
    <div
      role="region"
      aria-labelledby="no-password-hint-title"
      aria-live="polite"
      className="mt-4 overflow-hidden rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50/90 to-white px-4 py-4 shadow-[0_2px_8px_rgba(180,83,9,0.06)] sm:px-5 sm:py-4"
    >
      <div className="flex gap-3">
        <div
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100/90 text-amber-800"
          aria-hidden
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M12 8v4" />
            <path d="M12 16h.01" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p
            id="no-password-hint-title"
            className="text-sm font-semibold text-amber-950"
          >
            No password on this account yet
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-amber-950/80">
            Use{" "}
            <Link
              to="/reset-password"
              className="font-semibold text-[#00B894] underline decoration-[#00B894]/35 underline-offset-[3px] transition hover:decoration-[#00B894]"
            >
              Forgot password
            </Link>{" "}
            to get a secure link and create a password, then try signing in again.
          </p>
        </div>
      </div>
    </div>
  );
}

const Login = () => {
  const navigate = useNavigate();

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: true,
  });
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState("");
  const [signInGuidance, setSignInGuidance] = useState(
    /** @type {null | 'GOOGLE_ONLY_ACCOUNT' | 'NO_PASSWORD_SET'} */ (null)
  );
  const [loading, setLoading] = useState(false);
  const [emailValid, setEmailValid] = useState(true);

  // A refused Google sign-in comes back here with its reason in ?error=.
  // Shown as sent: formatAuthError is written for password sign-in, and turns
  // "use your password to log in" into a vaguer message and a Google failure
  // into "check your credentials" — which the applicant never typed.
  useRedirectError(useCallback((message) => setLoginError(message), []));

  useEffect(() => {
    // Task 149: a marker, not a credential — it only decides whether to skip
    // the login form. The API authorises everything from the cookie.
    if (isSignedIn()) {
      const next = consumePostLoginRedirect();
      navigate(next || "/dashboard");
    } else {
      setCheckingAuth(false);
    }
  }, [navigate]);

  const handleGoogleLogin = () => {
    window.location.href = googleLoginUrl;
  };

  if (checkingAuth) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? checked : name === "email" ? value.trim() : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setLoginError("");
    setSignInGuidance(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      setLoginError("");
      setSignInGuidance(null);

      // let userExists = false;
      // try {
      //   const res = await checkUserByEmail(formData.email);
      //   if (res?.status === 200 && res?.data) {
      //     userExists = true;
      //   }
      // } catch (err) {
      //   if (err?.response?.status === 204) {
      //     userExists = false;
      //   } else {
      //     console.error("Check user error:", err);
      //   }
      // }

      // if (!userExists) {
      //   setEmailValid(false);
      //   return;
      // } else {
      //   setEmailValid(true);
      // }

      const payload = {
        email: formData.email,
        password: formData.password,
      };

      const res = await login(payload);

      if (res?.data?.verificationRequired) {
        setEmailVerificationState({
          token: res.data.verificationToken,
          email: res.data.user.email,
          rememberMe: formData.rememberMe,
          expiresAt: res.data.expiresAt,
          resendAvailableAt: res.data.resendAvailableAt,
          serverTime: res.data.serverTime,
          clientReceivedAt: Date.now(),
          // Every password sign-in now asks for an emailed code; the server
          // says whether this one is a sign-in or a first verification.
          reason: res.data.verificationReason,
        });
        navigate("/verify-email", {
          state: { emailDeliveryError: Boolean(res.data.emailDeliveryError) },
        });
        return;
      }

      const token = res?.data?.token;
      const user = res?.data?.user;

      if (token && user) {
        // Task 149: the same response established an HttpOnly session cookie,
        // so the token is not stored. "Keep me signed in" is now the cookie's
        // own lifetime, decided by the server.
        setSignedIn();
        localStorage.setItem("email", formData.email);

        // Store user details
        localStorage.setItem("userDetails", JSON.stringify(user));

        const eligibilityData = localStorage.getItem("visaEligibilityAnswers");
        const answersRaw = JSON.parse(
          localStorage.getItem("visaEligibilityResponses")
        );
        const deepLink = consumePostLoginRedirect();
        if (deepLink) {
          navigate(deepLink);
        } else {
          navigate(
            eligibilityData || answersRaw ? "/answer-review" : "/dashboard"
          );
        }
      } else {
        const errorMsg =
          res?.data?.message ||
          res?.data?.error ||
          "Invalid email or password";
        setLoginError(formatAuthError(errorMsg, "login"));
      }
    } catch (err) {
      console.error("Login error:", err);
      const data = err?.response?.data;
      const code = data?.code;
      const backendError =
        data?.error ||
        data?.message ||
        err?.message ||
        "Login failed. Please try again.";

      if (code === "GOOGLE_ONLY_ACCOUNT") {
        setSignInGuidance("GOOGLE_ONLY_ACCOUNT");
        setLoginError("");
      } else if (code === "NO_PASSWORD_SET") {
        setSignInGuidance("NO_PASSWORD_SET");
        setLoginError("");
      } else {
        setSignInGuidance(null);
        setLoginError(formatAuthError(backendError, "login"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative login_main body_padding pb-[100px]">
      <h1 className="mb-0 text-center sm:mb-1">Welcome back</h1>
      <p className="text-center mb-8">Sign in to continue your application</p>

      <form
        className="bg-white rounded-xl border border-gray-200 shadow-sm sm:p-[35px] p-5 w-full max-w-[90%] sm:max-w-[500px] mx-auto login_form"
        onSubmit={handleSubmit}
      >
        <div className="mb-5">
          <label className="mt-2 flex text-sm font-semibold">Email</label>
          <input
            type="text"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            className={`border rounded-lg w-full py-[14px] px-[16px] text-base ${
              errors.email || emailValid === false
                ? "border-red-500"
                : "border-gray-200"
            }`}
            aria-invalid={!!errors.email || emailValid === false}
            aria-describedby="email-error"
            autoComplete="username"
          />

          {errors.email && (
            <span id="email-error" className="text-red-500 text-sm">
              {errors.email}
            </span>
          )}

          {emailValid === false && !errors.email && (
            <span className="text-red-500 text-sm">
              No account found with this email. Please sign up first.
            </span>
          )}
        </div>

        <div>
          <label className="mt-2 flex text-sm font-semibold">Password</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            autoComplete="current-password"
            placeholder="Enter your password"
            className={`border rounded-lg w-full py-[14px] px-[16px] text-base ${
              errors.password ? "border-red-500" : "border-gray-200"
            }`}
            aria-invalid={!!errors.password}
            aria-describedby="password-error"
          />
          {errors.password && (
            <span id="password-error" className="text-red-500 text-sm">
              {errors.password}
            </span>
          )}
        </div>

        {signInGuidance === "NO_PASSWORD_SET" && <NoPasswordHint />}

        {loginError && (
          <p className="text-red-500 text-sm mt-2">{loginError}</p>
        )}

        <div className="mt-5 flex flex-row items-center justify-between gap-3">
          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
            <span className="inline-flex h-5 w-[18px] flex-none items-center justify-center">
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
                aria-describedby="rememberMe-desc"
                className="m-0 h-[18px] w-[18px] rounded border border-[#1B365D] text-[#1B365D] accent-[#1B365D] focus:outline-none focus:ring-2 focus:ring-[#1B365D]/25"
              />
            </span>
            <span
              id="rememberMe-desc"
              className="text-sm leading-5 text-[#1B365D]"
            >
              Keep me signed in
            </span>
          </label>
          <Link
            to="/reset-password"
            className="shrink-0 text-sm font-semibold text-[#00B894]"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          style={buttonloginstyle}
          disabled={loading}
          className={`w-full mt-7 ${
            loading ? "cursor-not-allowed opacity-60" : ""
          }`}
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>

        {signInGuidance === "GOOGLE_ONLY_ACCOUNT" && <GoogleSignInHint />}

        <div className="mt-6 w-full">
          <GoogleSignInCustomButton
            label="Continue with Google"
            onClick={handleGoogleLogin}
            disabled={loading}
          />
        </div>

        <div className="mt-6 pt-5 border-t border-gray-200">
          <p className="text-center font-semibold">
            Don't have an account?{" "}
            <Link to="/sign-up" className="text-[#E76F51] font-bold">
              Sign Up
            </Link>
          </p>
        </div>
      </form>

      <div className="mt-6 flex justify-center items-center">
        <img src={secure} alt="secure login" className="me-3" />
        Secure login
      </div>
    </div>
  );
};

export default Login;
